import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { API } from "../../../App";
import { todayStr } from "./diUtils";
import { ChevronLeft, ChevronRight, CalendarCheck, List, LayoutGrid } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Horas em UTC, como o servidor. A grelha mostra pelo menos as 08:00–17:00 da maquete e alarga-se se houver mais.
const GRELHA_MIN = 8;
const GRELHA_MAX = 17;

const TIPO = {
  voo: { label: "Voo", cls: "bg-blue-500/30 text-blue-100 border-blue-400/40" },
  instrucao: { label: "Instrução", cls: "bg-teal-500/30 text-teal-100 border-teal-400/40" },
  passageiro: { label: "Passageiro", cls: "bg-purple-500/30 text-purple-100 border-purple-400/40" },
};

const addDays = (day, n) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const horaUtc = (iso) => new Date(iso).getUTCHours();
const hhmmUtc = (iso) => new Date(iso).toISOString().slice(11, 16);
// No telemóvel abre em lista (a grelha precisa de largura); a escolha fica guardada neste aparelho
const vistaInicial = () => {
  try {
    const guardada = window.localStorage.getItem("di-reservas-vista");
    if (guardada === "lista" || guardada === "grelha") return guardada;
  } catch { /* sem armazenamento: segue o ecrã */ }
  return typeof window !== "undefined" && window.innerWidth < 640 ? "lista" : "grelha";
};
const erro = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

export default function ReservationsPanel() {
  const navigate = useNavigate();
  const [day, setDay] = useState(todayStr());
  const [reservas, setReservas] = useState([]);
  const [aeronaves, setAeronaves] = useState([]);
  const [anomalias, setAnomalias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState(vistaInicial);
  const [aCancelar, setACancelar] = useState(null);
  const mudarVista = (v) => {
    setVista(v);
    try { window.localStorage.setItem("di-reservas-vista", v); } catch { /* ignora */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, air, anom] = await Promise.all([
        axios.get(`${API}/di/reservations`, { params: { date_from: day, date_to: day }, withCredentials: true }),
        axios.get(`${API}/di/aircraft`, { withCredentials: true }),
        axios.get(`${API}/di/reservations/anomalies`, { params: { date_from: todayStr(), date_to: day }, withCredentials: true }),
      ]);
      setReservas(res.data || []);
      setAeronaves(air.data || []);
      setAnomalias(anom.data || []);
    } catch (e) {
      toast.error(erro(e, "Não foi possível carregar as reservas."));
    } finally {
      setLoading(false);
    }
  }, [day]);

  useEffect(() => { load(); }, [load]);

  // As canceladas não ocupam a grelha; na lista aparecem no fim, riscadas
  const ativas = useMemo(() => reservas.filter((r) => r.status !== "cancelled"), [reservas]);
  const canceladas = useMemo(() => reservas.filter((r) => r.status === "cancelled"), [reservas]);

  const cancelar = async (r) => {
    if (aCancelar !== r.id) { setACancelar(r.id); return; }   // primeiro toque pede confirmação
    try {
      await axios.post(`${API}/di/reservations/${r.id}/cancel`, {}, { withCredentials: true });
      toast.success("Reserva cancelada.");
      setACancelar(null);
      await load();
    } catch (e) {
      toast.error(erro(e, "Não foi possível cancelar."));
    }
  };

  const horas = useMemo(() => {
    let a = GRELHA_MIN;
    let b = GRELHA_MAX;
    for (const r of ativas) {
      a = Math.min(a, horaUtc(r.starts_at));
      b = Math.max(b, horaUtc(r.starts_at));
    }
    return Array.from({ length: b - a + 1 }, (_, i) => a + i);
  }, [ativas]);

  const linhas = useMemo(() => {
    const regs = new Set(aeronaves.map((a) => a.registration).filter(Boolean));
    ativas.forEach((r) => regs.add(r.aircraft_registration));
    return [...regs].sort();
  }, [aeronaves, ativas]);

  const inicioGrelha = horas[0];

  return (
    <div className="space-y-4" data-testid="di-reservas-panel">
      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex flex-wrap items-center gap-2 text-orange-300">
            <CalendarCheck className="w-5 h-5" /> Reservas da escola
          </CardTitle>
          <div className="flex items-center gap-2 pt-2">
            <Button variant="outline" size="icon" className="shrink-0" aria-label="Dia anterior" onClick={() => setDay(addDays(day, -1))} data-testid="di-reservas-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="bg-slate-800 border-slate-700 min-w-0 flex-1 sm:flex-none sm:w-auto" data-testid="di-reservas-day" />
            <Button variant="outline" size="icon" className="shrink-0" aria-label="Dia seguinte" onClick={() => setDay(addDays(day, 1))} data-testid="di-reservas-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="shrink-0" onClick={() => setDay(todayStr())} data-testid="di-reservas-today">Hoje</Button>
            {loading && <span className="text-[11px] text-slate-500">A carregar…</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button size="sm" className="h-10 px-4" onClick={() => navigate("/reservas")} data-testid="di-reservas-nova">Nova reserva</Button>
            <div className="ml-auto flex rounded-md border border-slate-700 overflow-hidden">
              <button type="button" onClick={() => mudarVista("lista")} className={`h-10 px-3 flex items-center gap-1 text-xs ${vista === "lista" ? "bg-slate-700 text-white" : "text-slate-400"}`} data-testid="di-reservas-vista-lista">
                <List className="w-4 h-4" /> Lista
              </button>
              <button type="button" onClick={() => mudarVista("grelha")} className={`h-10 px-3 flex items-center gap-1 text-xs ${vista === "grelha" ? "bg-slate-700 text-white" : "text-slate-400"}`} data-testid="di-reservas-vista-grelha">
                <LayoutGrid className="w-4 h-4" /> Grelha
              </button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 pt-1">Horas em UTC.</p>
        </CardHeader>
        <CardContent className="space-y-3 text-xs">
          {ativas.length === 0 && !loading && (
            <p className="text-slate-400">Sem reservas neste dia.</p>
          )}
          {vista === "lista" && [...ativas, ...canceladas].map((r) => {
            const t = TIPO[r.kind] || TIPO.voo;
            const cancelada = r.status === "cancelled";
            return (
              <div key={r.id} className={`rounded-md border border-slate-800 p-3 space-y-1 ${cancelada ? "opacity-50" : ""}`} data-testid="di-reservas-item">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-semibold text-slate-100 ${cancelada ? "line-through" : ""}`}>
                    {hhmmUtc(r.starts_at)}–{hhmmUtc(r.ends_at)} · <span className="font-mono">{r.aircraft_registration}</span>
                  </span>
                  <span className={`shrink-0 rounded border px-2 py-0.5 text-[11px] ${t.cls}`}>{t.label}</span>
                </div>
                <div className="text-slate-300 text-[13px]">
                  {r.pilot_name || "—"}
                  {r.pic_name && r.pic_id !== r.pilot_id && <span className="text-slate-400"> · {r.pic_kind === "instructor" ? "com" : "PIC:"} {r.pic_name}</span>}
                </div>
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span className="text-[12px]">
                    {cancelada ? <span className="text-slate-400">Cancelada</span>
                      : r.flight_id ? <span className="text-emerald-300" data-testid="di-reservas-voado">✓ voado</span>
                        : <span className="text-slate-500">por voar</span>}
                  </span>
                  {!cancelada && !r.flight_id && (
                    <Button size="sm" variant={aCancelar === r.id ? "destructive" : "outline"} className="h-9 px-3" onClick={() => cancelar(r)} data-testid="di-reservas-cancelar">
                      {aCancelar === r.id ? "Confirmar cancelamento" : "Cancelar"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
          {vista === "grelha" && linhas.length > 0 && (
            <div className="overflow-x-auto">
              <div style={{ minWidth: 110 + horas.length * 46 }}>
                <div className="grid gap-1 items-center mb-1" style={{ gridTemplateColumns: `110px repeat(${horas.length}, minmax(0, 1fr))` }}>
                  <span />
                  {horas.map((h) => (
                    <span key={h} className="text-[11px] text-slate-500 text-center">{String(h).padStart(2, "0")}</span>
                  ))}
                </div>
                {linhas.map((reg) => (
                  <div key={reg} className="grid gap-1 items-center mb-1" style={{ gridTemplateColumns: `110px repeat(${horas.length}, minmax(0, 1fr))` }} data-testid={`di-reservas-linha-${reg}`}>
                    <span className="text-slate-200 font-mono text-[12px]">{reg}</span>
                    {ativas.filter((r) => r.aircraft_registration === reg).map((r) => {
                      const inicio = horaUtc(r.starts_at);
                      const dur = Math.max(1, Math.round((Date.parse(r.ends_at) - Date.parse(r.starts_at)) / 3600000));
                      const t = TIPO[r.kind] || TIPO.voo;
                      return (
                        <div
                          key={r.id}
                          className={`h-9 rounded border flex items-center px-1.5 text-[11px] overflow-hidden whitespace-nowrap ${t.cls}`}
                          style={{ gridColumn: `${inicio - inicioGrelha + 2} / span ${dur}` }}
                          title={`${t.label} · ${r.pilot_name || ""}${r.pic_name ? ` · PIC ${r.pic_name}` : ""}${r.flight_id ? " · voado (voo validado)" : ""}`}
                          data-testid="di-reservas-bloco"
                        >
                          {t.label} · {r.pilot_name || r.pic_name || "—"}{r.flight_id && <span data-testid="di-reservas-voado"> · ✓ voado</span>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-3 text-[11px] text-slate-400 pt-1">
            {Object.values(TIPO).map((t) => (
              <span key={t.label} className="flex items-center gap-1.5"><span className={`inline-block w-2.5 h-2.5 rounded border ${t.cls}`} />{t.label}</span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="instrument-panel">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-amber-300">Anomalias por validar</CardTitle>
          <p className="text-[11px] text-slate-500">Voos a partir de hoje (UTC) sem reserva ativa no mesmo dia, aeronave e piloto ou instrutor. Não bloqueiam o registo do voo.</p>
        </CardHeader>
        <CardContent className="space-y-2 text-xs" data-testid="di-reservas-anomalias">
          {anomalias.length === 0 && <p className="text-slate-400">Sem anomalias nestes dias.</p>}
          {anomalias.map((a) => (
            <div key={a.flight_id} className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-2" data-testid="di-reservas-anomalia">
              <span className="text-slate-200">
                {a.flight_date} · <span className="font-mono">{a.aircraft_registration || "sem aeronave"}</span> · {a.person_name} · {a.flight_type || "voo"}
                {a.navigation_log_number ? ` · diário ${a.navigation_log_number}/${a.service_number || ""}` : ""}
              </span>
              <span className="text-amber-300">voo sem reserva</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
